import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { useEffect } from 'react';

import { useGetFcmToken } from '@/features/notifications/atoms/fcmToken';
import {
  refreshNotificationPermission,
  useGetNotificationPermission,
} from '@/features/notifications/atoms/notificationPermission';
import { useRegisterDevice } from '@/features/notifications/hooks/useRegisterDevice';
import { getPushToken } from '@/features/notifications/utils/pushNotifications';
import { getPlatform, isNativePlatform } from '@/shared/utils/platform';

export const useNotificationPermissionInit = () => {
  const { mutateAsync: registerDevice } = useRegisterDevice();
  const hasPermission = useGetNotificationPermission();
  const fcmToken = useGetFcmToken();

  // Effect: Refresh token when permission is granted
  useEffect(() => {
    if (!hasPermission || !fcmToken) return;

    /**
     * Silently refresh FCM token if it has changed.
     * Handles stale tokens from FCM rotation, OS updates, or inactivity.
     */
    const refreshTokenIfNeeded = async () => {
      try {
        const currentToken = await getPushToken(getPlatform(), {
          silent: true,
        });
        if (currentToken === fcmToken) return;

        await registerDevice({
          platform: getPlatform(),
          source: 'token_refresh',
          token: currentToken,
          silent: true, // Background refresh - no toasts
        });
      } catch {
        // Silent failure - don't disrupt user experience
      }
    };

    void refreshTokenIfNeeded();
  }, [hasPermission, registerDevice, fcmToken]);

  // Effect: Initialize permission state and listen for changes
  useEffect(() => {
    void refreshNotificationPermission();

    if (isNativePlatform()) {
      let listener: PluginListenerHandle | undefined;

      void App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          void refreshNotificationPermission();
        }
      }).then(result => {
        listener = result;
      });

      return () => {
        void listener?.remove();
      };
    } else {
      // Web: Permissions API
      let status: PermissionStatus | undefined;

      void navigator.permissions
        ?.query({ name: 'notifications' })
        .then(result => {
          status = result;
          status.onchange = () => {
            void refreshNotificationPermission();
          };
        })
        .catch(() => {
          /* empty */
        });

      return () => {
        if (status) {
          status.onchange = null;
        }
      };
    }
  }, []);
};
